/**
 * Репозитории GitLab и состояние вебхука на каждом (DEV-1581).
 *
 * Групповой хук у GitLab платный, поэтому он нужен на каждом репозитории.
 * Ставит их сверка на бэкенде — экран показывает её результат и даёт нажать
 * то же самое немедленно.
 */
import { devopsApi } from '@/api'

import { DevOpsServiceListRepositoriesStateEnum } from '@/api/clients/tracker-gitlab'

import type { V1Repository } from '@/api/clients/tracker-gitlab'

/** Состояние вебхука в понятиях экрана. */
export type WebhookState = 'connected' | 'missing' | 'failed' | 'muted'

/** Строка таблицы репозиториев. */
export interface Repository {
  id: number
  path: string
  webUrl: string
  archived: boolean
  state: WebhookState
  hookId: number
  error: string
  lastEventAt: string
  syncedAt: string
}

/** Числа над таблицей. Считаются по всей группе, не по отфильтрованным строкам. */
export interface RepositoryTotals {
  total: number
  connected: number
  missing: number
  failed: number
  syncedAt: string
}

/** Итог сверки — то, что показывается тостом после кнопки. */
export interface SyncReport {
  checked: number
  connected: number
  repaired: number
  failed: number
  failures: { path: string; message: string }[]
  totals: RepositoryTotals
}

const STATE_BY_PROTO: Record<string, WebhookState> = {
  WEBHOOK_STATE_CONNECTED: 'connected',
  WEBHOOK_STATE_MISSING: 'missing',
  WEBHOOK_STATE_FAILED: 'failed',
  WEBHOOK_STATE_MUTED: 'muted',
}

const PROTO_BY_STATE: Record<WebhookState, DevOpsServiceListRepositoriesStateEnum> = {
  connected: DevOpsServiceListRepositoriesStateEnum.WebhookStateConnected,
  missing: DevOpsServiceListRepositoriesStateEnum.WebhookStateMissing,
  failed: DevOpsServiceListRepositoriesStateEnum.WebhookStateFailed,
  muted: DevOpsServiceListRepositoriesStateEnum.WebhookStateMuted,
}

/** Порядок состояний в фильтре: сперва то, что требует внимания. */
export const WEBHOOK_STATES: WebhookState[] = ['missing', 'failed', 'connected', 'muted']

const toRepository = (raw: V1Repository): Repository => ({
  id: Number(raw.gitlabProjectId ?? 0),
  path: raw.path ?? '',
  webUrl: raw.webUrl ?? '',
  archived: raw.archived ?? false,
  state: STATE_BY_PROTO[String(raw.state ?? '')] ?? 'missing',
  hookId: Number(raw.hookId ?? 0),
  error: raw.error ?? '',
  lastEventAt: raw.lastEventAt ?? '',
  syncedAt: raw.syncedAt ?? '',
})

const toTotals = (raw: {
  total?: number
  connected?: number
  missing?: number
  failed?: number
  syncedAt?: string
}): RepositoryTotals => ({
  total: raw.total ?? 0,
  connected: raw.connected ?? 0,
  missing: raw.missing ?? 0,
  failed: raw.failed ?? 0,
  syncedAt: raw.syncedAt ?? '',
})

/** Список репозиториев и итоги одним запросом — как их и отдаёт сервис. */
export const listRepositories = async (
  state?: WebhookState,
): Promise<{ repositories: Repository[]; totals: RepositoryTotals }> => {
  const { data } = await devopsApi.devOpsServiceListRepositories(
    state ? PROTO_BY_STATE[state] : undefined,
  )

  return {
    repositories: (data.repositories ?? []).map(toRepository),
    totals: toTotals(data.totals ?? {}),
  }
}

/** Сверка по кнопке: та же, что идёт по расписанию. */
export const syncRepositories = async (): Promise<SyncReport> => {
  const { data } = await devopsApi.devOpsServiceSyncRepositories({})

  return {
    checked: data.checked ?? 0,
    connected: data.connected ?? 0,
    repaired: data.repaired ?? 0,
    failed: data.failed ?? 0,
    failures: (data.failures ?? []).map((failure) => ({
      path: failure.gitlabProjectPath ?? '',
      message: failure.message ?? '',
    })),
    totals: toTotals(data.totals ?? {}),
  }
}

/** Поставить вебхук на один репозиторий. */
export const connectRepository = async (id: number): Promise<Repository> => {
  const { data } = await devopsApi.devOpsServiceConnectRepository(String(id), {})

  return toRepository(data.repository ?? {})
}

/** Снять вебхук. Сверка такой репозиторий больше не трогает. */
export const disconnectRepository = async (id: number): Promise<Repository> => {
  const { data } = await devopsApi.devOpsServiceDisconnectRepository(String(id), {})

  return toRepository(data.repository ?? {})
}
